<script setup lang="ts">
// NOTE: vue/no-undef-properties does not work when props interface
// is defined in different file than compnent, hence must be switched off.
// Props interface have to be in different file to reexport it in ui/lib/index.ts

/* eslint-disable vue/no-undef-properties */

import { computed, ref, watch } from 'vue';
// eslint-disable-next-line import/default
import Slider from '@vueform/slider';

import type { AppSliderProps } from './AppSlider.model';

const props = defineProps<AppSliderProps>();

interface AppSliderEmits {
  (event: 'update-value', value: number): void;
  (event: 'update-range', value: number[]): void;
  (event: 'slide-value', value: number): void;
  (event: 'slide-range', value: number[]): void;
}

const emit = defineEmits<AppSliderEmits>();

const value = ref<number[] | number>(props.initialValue);

const shouldHandleBeGrey = computed(() =>
  Array.isArray(value.value)
    ? value.value[0] === props.min && value.value[1] === props.max
    : value.value === props.max
);

// NOTE: This event is emitted when user releases mouse button,
// this represents value change
function handleUpdate(value: number | number[]): void {
  Array.isArray(value)
    ? emit('update-range', value)
    : emit('update-value', value);
}

// NOTE: This event is emitted when user drags slider handle,
// this represents possible future value
function handleSlide(value: number | number[]): void {
  Array.isArray(value)
    ? emit('slide-range', value)
    : emit('slide-value', value);
}

watch(props, () => {
  value.value = props.initialValue;
});
</script>

<template>
  <Slider
    :id="id"
    v-model="value"
    class="app-slider range-slider slider-target slider-ltr slider-horizontal slider-txt-dir-ltr"
    :class="{
      'grey-handle': shouldHandleBeGrey,
    }"
    :min="props.min"
    :max="props.max"
    :step="props.step"
    :lazy="false"
    @change="handleUpdate(value)"
    @slide="handleSlide(value)"
  />
</template>

<style lang="scss">
.app-slider {
  margin: 20px;
  z-index: 0;

  .slider-base {
    pointer-events: none;

    .slider-origin {
      pointer-events: all;
    }
  }

  &.grey-handle {
    .slider-handle {
      border: 10px solid var(--color-secondary-80);
    }

    .slider-connect {
      background: var(--color-secondary-90);
    }
  }
}

:root {
  --slider-bg: var(--color-primary-20);
  --slider-connect-bg: var(--color-primary-20);
  --slider-height: 3px;
  --slider-handle-bg: var(--color-white);
  --slider-handle-border: 10px solid var(--color-primary-20);
  --slider-handle-width: 35px;
  --slider-handle-height: 35px;
  --slider-handle-shadow: none;
  --slider-handle-shadow-active: none;
  --slider-tooltip-font-size: 0;
  --slider-tooltip-line-height: 0;
  --slider-tooltip-font-weight: 0;
  --slider-tooltip-min-width: 0;
  --slider-tooltip-bg: transparent;
  --slider-tooltip-bg-disabled: transparent;
  --slider-tooltip-color: transparent;
  --slider-tooltip-radius: 0;
  --slider-tooltip-py: 0;
  --slider-tooltip-px: 0;
  --slider-tooltip-arrow-size: 0;
  --slider-tooltip-distance: 0;
}
</style>

<style src="@vueform/slider/themes/default.scss" lang="scss"></style>
